/**
 * The specific-clients selector as plain data (ACR-1596): a searchable list, a
 * selection shown as removable chips, and no cap on how many clients can be
 * selected (spec: No cap on clients). The selection is an ordered list of tenant
 * ids; every function returns a new one.
 */

export interface ClientOption {
    readonly tenantId: string;
    readonly displayName: string;
    readonly slug?: string | undefined;
    readonly subdomain?: string | null | undefined;
}

export interface SelectionChip {
    readonly tenantId: string;
    readonly label: string;
}

/** Clients whose name, slug, subdomain or id contains the search, ignoring case. */
export function filterClients<T extends ClientOption>(
    clients: readonly T[],
    search: string,
): readonly T[] {
    const term = search.trim().toLowerCase();
    if (term === '') return clients;
    return clients.filter((client) =>
        [client.displayName, client.slug, client.subdomain, client.tenantId].some((field) =>
            field?.toLowerCase().includes(term),
        ),
    );
}

export function selectClients(
    selected: readonly string[],
    tenantIds: readonly string[],
): readonly string[] {
    const seen = new Set(selected);
    const added = tenantIds.filter((id) => {
        if (seen.has(id)) return false;
        seen.add(id);
        return true;
    });
    return added.length === 0 ? selected : [...selected, ...added];
}

export function selectClient(selected: readonly string[], tenantId: string): readonly string[] {
    return selectClients(selected, [tenantId]);
}

/** Removing a chip deselects that client. */
export function deselectClient(selected: readonly string[], tenantId: string): readonly string[] {
    return selected.filter((id) => id !== tenantId);
}

/** One chip per selected client, labelled by name; an id no longer in the list keeps the id. */
export function selectionChips(
    selected: readonly string[],
    clients: readonly ClientOption[],
): readonly SelectionChip[] {
    const names = new Map(clients.map((client) => [client.tenantId, client.displayName]));
    return selected.map((tenantId) => ({ tenantId, label: names.get(tenantId) ?? tenantId }));
}
